import React, { useEffect, useMemo, useState } from "react";
import { Loader, MultiSelect, Stack, Text } from "@mantine/core";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { TaxRate } from "../../types/taxRates";

interface Props {
  /**
   * Current tax_rate IDs linked to the product/service being edited.
   * Shape: integer PKs from tax_rates (not UUIDs).
   */
  value: number[];
  /** Updates the parent's local state with the new selection. */
  onChange: (nextValue: number[]) => void;
  /** Optional helper text shown under the MultiSelect. */
  description?: string;
  /** Label override; defaults to "Applicable Tax Rates". */
  label?: string;
  disabled?: boolean;
}

/**
 * Reusable Mantine MultiSelect for picking tax rates on a product or service
 * edit form. Fetches the salon's active tax rates on mount via AppContext and
 * filters out any rate that has already been end-dated (effective_end_date
 * in the past) so operators don't accidentally attach stale rates.
 *
 * The value prop is the list of integer tax_rate_id PKs. The parent is
 * expected to call AppContext.setProductTaxRates / setSalonServiceTaxRates
 * with the final array when it saves.
 */
export default function TaxRateMultiSelect({
  value,
  onChange,
  description,
  label = "Applicable Tax Rates",
  disabled = false,
}: Props) {
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const { getTaxRates } = useApp();

  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      if (!businessInfo?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await getTaxRates(businessInfo.id);
        if (!cancelled) {
          setTaxRates(Array.isArray(response) ? response : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error("Failed to load tax rates:", err);
          setError("Failed to load tax rates");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetch();

    return () => {
      cancelled = true;
    };
  }, [businessInfo?.id, getTaxRates]);

  const options = useMemo(() => {
    const today = new Date();
    return taxRates
      .filter((rate) => {
        if (!rate.effective_end_date) return true;
        return new Date(rate.effective_end_date) > today;
      })
      .map((rate) => ({
        // Mantine MultiSelect values must be strings
        value: String(rate.id),
        label: `${rate.name} — ${(rate.rate * 100).toFixed(3)}%`,
      }));
  }, [taxRates]);

  const stringValue = useMemo(() => value.map((v) => String(v)), [value]);

  const handleChange = (next: string[]) => {
    onChange(next.map((s) => parseInt(s, 10)).filter((n) => !isNaN(n)));
  };

  if (isLoading) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Loader size="xs" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="red">
          {error}
        </Text>
      </Stack>
    );
  }

  return (
    <MultiSelect
      label={label}
      description={
        description ||
        "Tax rates that automatically apply to this item. Manage rates under Settings → Taxes."
      }
      data={options}
      value={stringValue}
      onChange={handleChange}
      searchable
      clearable
      disabled={disabled}
      nothingFoundMessage="No tax rates configured"
    />
  );
}
